import { useMemo, type CSSProperties, type RefObject } from "react";
import { displayUrl, languageTag, type AppEntry } from "../registry/apps";
import { QUEST_GOAL } from "../lib/progress";
import { formatInt } from "../lib/format";
import { buildWall, orbsInView, tilesInView } from "../lib/wall-layout";
import { useWallCamera } from "../lib/use-wall-camera";

type MosaicProps = {
  apps: readonly AppEntry[];
  progressCount: number;
  progressRemaining: number;
  reducedMotion: boolean;
  interactive: boolean;
  onOpenApp: (app: AppEntry) => void;
};

export function Mosaic({
  apps,
  progressCount,
  progressRemaining,
  reducedMotion,
  interactive,
  onOpenApp,
}: MosaicProps) {
  const layout = useMemo(() => buildWall(apps), [apps]);
  const { viewportRef, worldRef, view, dragging, dragRef } = useWallCamera(
    layout,
    reducedMotion,
    interactive,
  );

  const visible = useMemo(
    () => tilesInView(layout.tiles, view),
    [layout.tiles, view],
  );
  const orbs = useMemo(
    () => orbsInView(layout.orbs, view),
    [layout.orbs, view],
  );

  return (
    <div
      ref={viewportRef}
      className={`mosaic-viewport ${dragging ? "is-dragging" : ""} ${reducedMotion ? "is-still" : ""}`}
      role="region"
      aria-label="Mosaic wall of live apps. Drag or scroll to pan. Pinch or control plus mouse wheel to zoom. Arrow keys pan, plus and minus zoom, zero resets. Click a tile for details. APPS opens the registry."
    >
      <div
        ref={worldRef}
        className="mosaic-world"
        style={{ width: layout.width, height: layout.height }}
      >
        {orbs.map((orb) => (
          <span
            key={orb.key}
            className={`space-orb tone-${orb.tone} ${reducedMotion ? "is-still" : ""}`}
            style={{
              left: orb.x,
              top: orb.y,
              width: orb.size,
              height: orb.size,
            }}
            aria-hidden="true"
          />
        ))}

        {visible.map((tile) => {
          const style = {
            left: tile.x,
            top: tile.y,
            width: tile.w,
            height: tile.h,
            "--tilt": `${tile.tilt}deg`,
          } as CSSProperties;

          if (tile.kind === "score") {
            return (
              <article key={tile.key} className="tile score" style={style}>
                <p className="score-kicker">
                  {progressCount === 0 ? "DAY ZERO" : "LIVE COUNT"}
                </p>
                <p className="score-num">
                  <span>{formatInt(progressCount)}</span>
                  <span className="score-of">/{formatInt(QUEST_GOAL)}</span>
                </p>
                <p className="score-foot">
                  {progressCount === 0
                    ? "The wall is empty. Start shipping."
                    : `${formatInt(progressRemaining)} still to ship.`}
                </p>
              </article>
            );
          }

          return (
            <AppTile
              key={tile.key}
              app={tile.app}
              style={style}
              dragRef={dragRef}
              onOpen={onOpenApp}
            />
          );
        })}
      </div>
    </div>
  );
}

function AppTile({
  app,
  style,
  dragRef,
  onOpen,
}: {
  app: AppEntry;
  style: CSSProperties;
  dragRef: RefObject<boolean>;
  onOpen: (app: AppEntry) => void;
}) {
  const href = displayUrl(app);
  const blurb = app.blurb?.trim() || "Live app on the path door.";
  const tag = languageTag(app.language);

  return (
    <button
      type="button"
      className="tile app"
      style={style}
      draggable={false}
      aria-haspopup="dialog"
      aria-label={`${app.title}.${tag ? ` ${tag}.` : ""} ${blurb} Show details.`}
      onClick={(event) => {
        if (dragRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onOpen(app);
      }}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <img
        src={app.screenshot}
        alt=""
        width={1440}
        height={900}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      <span className="tile-badges">
        <span className="tile-surface">{app.surface}</span>
        {tag ? <span className="tile-lang">{tag}</span> : null}
      </span>
      <div className="tile-meta">
        <span className="tile-title">{app.title}</span>
        <span className="tile-url" title={href}>
          {href}
        </span>
      </div>
      <span className="stamp live">DETAILS</span>
    </button>
  );
}
